import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../firebase';
import { toast } from 'react-toastify';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deactivationFilter, setDeactivationFilter] = useState('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Selection state
  const [selectedUsers, setSelectedUsers] = useState(new Set());

  const db = getDatabase(app);

  // Filter users based on criteria - memoized to avoid recalculation
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesRole = selectedRole === 'all' || user.role === selectedRole;
      const matchesSearch = user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase());

      // Add filter for auto-deactivation
      const matchesDeactivation =
        deactivationFilter === 'all' ||
        (deactivationFilter === 'auto-deactivated' && user.autoDeactivatedAt) ||
        (deactivationFilter === 'not-auto-deactivated' && !user.autoDeactivatedAt);

      return matchesRole && matchesSearch && matchesDeactivation;
    });
  }, [users, selectedRole, searchTerm, deactivationFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedRole, searchTerm, deactivationFilter, itemsPerPage]);

  // Clear selections when filters or pagination changes
  useEffect(() => {
    setSelectedUsers(new Set());
  }, [selectedRole, searchTerm, deactivationFilter, currentPage, itemsPerPage]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const [employersSnap, candidatesSnap] = await Promise.all([
          get(ref(db, 'employers')),
          get(ref(db, 'candidates'))
        ]);

        let allUsers = [];

        if (employersSnap.exists()) {
          const employers = Object.entries(employersSnap.val()).map(([id, data]) => ({
            id,
            ...data,
            fullName: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
            role: 'employer',
            profileStatus: data.profileStatus || 'active',
            isPublic: data.isPublic !== false,
            lastLoginAt: data.lastLoginAt || null,
            createdAt: data.createdAt || 'N/A',
            updatedAt: data.updatedAt || data.createdAt || 'N/A',
            autoDeactivatedAt: data.autoDeactivatedAt || null
          }));
          allUsers = [...allUsers, ...employers];
        }

        if (candidatesSnap.exists()) {
          const candidates = Object.entries(candidatesSnap.val()).map(([id, data]) => ({
            id,
            ...data,
            fullName: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
            role: 'candidate',
            profileStatus: data.profileStatus || 'active',
            isPublic: data.isPublic !== false,
            lastLoginAt: data.lastLoginAt || null,
            createdAt: data.createdAt || 'N/A',
            updatedAt: data.updatedAt || data.createdAt || 'N/A',
            autoDeactivatedAt: data.autoDeactivatedAt || null
          }));
          allUsers = [...allUsers, ...candidates];
        }

        setUsers(allUsers);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error('Error loading users');
        setLoading(false);
      }
    };

    fetchUsers();
  }, [db]);

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all users on current page
      const newSelected = new Set(paginatedUsers.map(user => user.id));
      setSelectedUsers(newSelected);
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const isAllSelected = paginatedUsers.length > 0 &&
    paginatedUsers.every(user => selectedUsers.has(user.id));

  const isSomeSelected = paginatedUsers.some(user => selectedUsers.has(user.id));

  const handleDeleteUser = async (userId, role) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        const functions = getFunctions();
        const deleteUserFunction = httpsCallable(functions, 'deleteUser');

        const loadingToast = toast.loading('Deleting user...');

        const result = await deleteUserFunction({
          userId: userId,
          userType: role === 'employer' ? 'employer' : 'candidate'
        });

        if (result.data.success) {
          toast.update(loadingToast, {
            render: 'User deleted successfully',
            type: 'success',
            isLoading: false,
            autoClose: 3000
          });

          setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
          setSelectedUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(userId);
            return newSet;
          });
        } else {
          toast.update(loadingToast, {
            render: `Error: ${result.data.message || 'Could not delete user'}`,
            type: 'error',
            isLoading: false,
            autoClose: 5000
          });
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        const errorMessage = error.message || 'Unknown error occurred';
        toast.error(`Error deleting user: ${errorMessage}`);
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedUsers.size === 0) {
      toast.warning('No users selected');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedUsers.size} selected user(s)? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;

    const loadingToast = toast.loading(`Deleting ${selectedUsers.size} users...`);
    let successCount = 0;
    let errorCount = 0;

    const functions = getFunctions();
    const deleteUserFunction = httpsCallable(functions, 'deleteUser');

    for (const userId of selectedUsers) {
      const user = users.find(u => u.id === userId);
      if (!user) continue;

      try {
        const result = await deleteUserFunction({
          userId: userId,
          userType: user.role === 'employer' ? 'employer' : 'candidate'
        });

        if (result.data.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error(`Error deleting user ${userId}:`, error);
        errorCount++;
      }
    }

    // Update local state
    setUsers(prevUsers => prevUsers.filter(user => !selectedUsers.has(user.id)));
    setSelectedUsers(new Set());

    toast.update(loadingToast, {
      render: `Deleted ${successCount} user(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
      type: errorCount > 0 ? 'warning' : 'success',
      isLoading: false,
      autoClose: 3000
    });
  };

  const handleDownloadUsers = () => {
    try {
      const dataToExport = filteredUsers.map(user => ({
        Name: user.fullName || 'N/A',
        Email: user.email || 'N/A',
        Role: user.role || 'N/A',
        Status: user.isPublic ? 'Active (Public)' : 'Inactive (Private)',
        CreatedAt: user.createdAt ? formatDate(user.createdAt) : 'N/A',
        UpdatedAt: user.updatedAt ? formatDate(user.updatedAt) : 'N/A',
        AutoDeactivatedAt: user.autoDeactivatedAt ? formatDate(user.autoDeactivatedAt) : 'N/A'
      }));

      const headers = Object.keys(dataToExport[0]);
      const csvRows = [];

      csvRows.push(headers.join(','));

      for (const row of dataToExport) {
        const values = headers.map(header => {
          const value = row[header] || '';
          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvRows.push(values.join(','));
      }

      const csvString = csvRows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `users_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('User list downloaded successfully');
    } catch (error) {
      console.error('Error downloading users:', error);
      toast.error('Error downloading user list');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';

      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[date.getMonth()];
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${month} ${day}, ${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const handleRunDeactivationCheck = async () => {
    if (window.confirm('Are you sure you want to run the profile deactivation check? This will deactivate profiles that have not been updated in 30+ days.')) {
      try {
        const functions = getFunctions();
        const checkDeactivationFunction = httpsCallable(functions, 'manualProfileDeactivationCheck');

        const loadingToast = toast.loading('Running profile deactivation check...');

        const result = await checkDeactivationFunction();

        toast.update(loadingToast, {
          render: `Deactivation check complete. ${result.data.deactivatedProfiles.employers + result.data.deactivatedProfiles.candidates} profiles deactivated.`,
          type: 'success',
          isLoading: false,
          autoClose: 5000
        });

        window.location.reload();

      } catch (error) {
        console.error('Error running deactivation check:', error);
        const errorMessage = error.message || 'Unknown error occurred';
        toast.error(`Error: ${errorMessage}`);
      }
    }
  };

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          className={`px-3 py-1 rounded ${
            currentPage === i
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-950"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-blue-950 mb-6">User Management</h2>

      {/* Filters and actions */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by name or email..."
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
        >
          <option value="all">All Users</option>
          <option value="employer">Employers</option>
          <option value="candidate">Candidates</option>
        </select>
        <select
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={deactivationFilter}
          onChange={(e) => setDeactivationFilter(e.target.value)}
        >
          <option value="all">All Deactivation Status</option>
          <option value="auto-deactivated">Auto-Deactivated Only</option>
          <option value="not-auto-deactivated">Not Auto-Deactivated</option>
        </select>
      </div>

      {/* Actions bar */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        <div className="flex items-center gap-4">
          <button
            onClick={handleRunDeactivationCheck}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Run Deactivation Check
          </button>

          {selectedUsers.size > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Selected ({selectedUsers.size})
            </button>
          )}
        </div>

        <button
          onClick={handleDownloadUsers}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
          disabled={filteredUsers.length === 0}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Download Users
        </button>
      </div>

      {/* Items per page selector */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show</span>
          <select
            className="px-3 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
        </div>
        <div className="text-sm text-gray-600">
          Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} entries
          {selectedUsers.size > 0 && (
            <span className="ml-2 text-blue-600">({selectedUsers.size} selected)</span>
          )}
        </div>
      </div>

      {/* Users Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = isSomeSelected && !isAllSelected;
                    }
                  }}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Profile Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Visibility
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created At
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Auto-Deactivated
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedUsers.map((user) => (
              <tr
                key={user.id}
                className={`${user.profileStatus === 'inactive' ? "bg-amber-50" : ""} ${selectedUsers.has(user.id) ? "bg-blue-50" : ""}`}
              >
                <td className="px-4 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedUsers.has(user.id)}
                    onChange={() => handleSelectUser(user.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  />
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{user.fullName || 'N/A'}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{user.email}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.role === 'employer' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.profileStatus === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.profileStatus === 'active' ? 'Active' : 'Inactive'}
                  </span>
                  <div className="text-xs text-gray-500 mt-1">System-managed</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.isPublic ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user.isPublic ? 'Public' : 'Private'}
                  </span>
                  <div className="text-xs text-gray-500 mt-1">User-controlled</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{formatDate(user.createdAt)}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {user.autoDeactivatedAt ? (
                    <div className="text-sm text-amber-700 font-medium">{formatDate(user.autoDeactivatedAt)}</div>
                  ) : (
                    <div className="text-sm text-gray-500">No</div>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleDeleteUser(user.id, user.role)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete user"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {paginatedUsers.length === 0 && (
        <div className="text-center py-4">
          <p className="text-gray-500">No users found</p>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            First
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Prev
          </button>

          {renderPaginationButtons()}

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Last
          </button>
        </div>
      )}

      {/* Information panel */}
      <div className="mt-6 p-4 bg-amber-50 rounded-md border border-amber-200">
        <h3 className="text-lg font-medium text-amber-800 mb-2">Auto-Deactivation Information</h3>
        <ul className="text-sm text-amber-700 space-y-1 ml-4">
          <li>• Profiles are automatically deactivated after 30 days of inactivity</li>
          <li>• Auto-deactivated profiles are highlighted in amber</li>
          <li>• Reactivating a profile will clear its auto-deactivation status</li>
          <li>• The system runs a daily check at midnight to deactivate inactive profiles</li>
          <li>• You can manually trigger a deactivation check using the "Run Deactivation Check" button</li>
        </ul>
      </div>
    </div>
  );
};

export default UserManagement;
